import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { tokens } from '../../theme';

export interface QuotaMeterProps {
  usedMB: number;
  totalMB?: number;
  onUpsell?: () => void;
  upsellThreshold?: number;
}

// Progress-track fill behind the bar — no exact token equivalent (sits between
// surface.raisedHi #21242c and border #26292B); kept as a literal.
const TRACK_BG = '#2b2f36';

const upsellText = (pct: number): string => {
  if (pct >= 100) return 'Storage full — get 5 GB';
  if (pct >= 80) return 'Running low — get 5 GB';
  return 'Sign up for 5 GB';
};

const QuotaMeter: React.FC<QuotaMeterProps> = ({
  usedMB,
  totalMB = 100,
  onUpsell,
  upsellThreshold = 60,
}) => {
  const pct = Math.min(100, Math.max(0, Math.round((usedMB / totalMB) * 100)));
  const tone = pct >= 100 ? 'full' : pct >= 80 ? 'warn' : 'ok';
  const accent =
    tone === 'full'
      ? tokens.color.danger
      : tone === 'warn'
        ? tokens.color.warn
        : tokens.color.accent;
  // Soft circular icon tint. The "ok" tint has an exact token (accentSoft);
  // warn/full have no token equivalent, so their rgba tints are kept literal.
  const tint =
    tone === 'full'
      ? 'rgba(229,87,92,0.16)'
      : tone === 'warn'
        ? 'rgba(227,165,73,0.16)'
        : tokens.color.accentSoft;
  const showUpsell = pct >= upsellThreshold;
  // Once the guest is at/near the cap, the meter is an upsell surface — lift the
  // panel border to the tone accent so the near-full state reads clearly.
  const emphasise = tone !== 'ok';

  return (
    <div
      className="rounded-[12px] p-[14px_15px] font-sans"
      style={{
        background: tokens.color.surface.raised,
        border: `1px solid ${emphasise ? accent : tokens.color.borderSoft}`,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="grid h-9 w-9 flex-none place-items-center rounded-full"
          style={{ background: tint, color: accent }}
        >
          <CloudOutlinedIcon style={{ fontSize: 19 }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex justify-between text-[13px] text-body">
            <div>
              <b className="font-semibold text-white">{usedMB} MB</b>{' '}
              <span className="tabular-nums text-muted">of {totalMB} MB</span>
            </div>
          </div>
          <div
            aria-hidden
            className="h-[6px] overflow-hidden rounded-[4px]"
            style={{ background: TRACK_BG }}
          >
            <div
              className="block h-full rounded-[4px]"
              style={{ width: `${pct}%`, background: accent }}
            />
          </div>
        </div>
      </div>
      {showUpsell && (
        <div
          role="button"
          tabIndex={0}
          onClick={onUpsell}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onUpsell?.();
          }}
          className="mt-3 flex items-center gap-[7px] pt-3 text-[12.5px] font-semibold"
          style={{
            borderTop: `1px solid ${tokens.color.borderSoft}`,
            color: accent,
            cursor: onUpsell ? 'pointer' : 'default',
          }}
        >
          {upsellText(pct)}
          <ArrowForwardIcon style={{ fontSize: 14 }} />
        </div>
      )}
    </div>
  );
};

export default QuotaMeter;
